// dependency-graph-ignore-file
// @ts-nocheck -- handler types use any for change shape; to be refined
import {
  calculationToCollabMapping,
  CardWidthToCollabMapping,
  coverFieldIdToCollabMapping,
  filterToCollabJSON,
  filterUsageTypeToCollabMapping,
  groupSortTypeToCollabMapping,
  CardTypeToCollabMapping
} from '@databases/collab/convert/to-collab';
import { getDirectParentFilter } from '@databases/filters/utils/getDirectParentFilter';
import { getNestedFilter } from '@databases/filters/utils/getNestedFilter';
import { type Database } from '@databases/model/database';
import {
  CollabFilterType,
  type MutableCollabFilter,
  type MutableCollabFilterBooleanExpression
} from '@databases/model/views/view-filter';
import { DatabaseFilterType } from '@databases/types/database/view';
import { v4 as uuidv4 } from 'uuid';

import { applyDeltaValue } from '../utils/apply-delta-value.ts';

import { ViewChangeType } from './view-change-types.ts';

export type ViewChange =
  | { type: ViewChangeType.UPDATE_LAYOUT_TYPE; viewId: string; newLayoutType: any }
  | { type: ViewChangeType.UPDATE_LAYOUT_CARD_WIDTH; viewId: string; newCardWidth: any }
  | {
      type: ViewChangeType.UPDATE_LAYOUT_COVER_FIELD_ID;
      viewId: string;
      newCoverFieldId: any;
    }
  | {
      type: ViewChangeType.UPDATE_LAYOUT_SHOW_FIELD_NAMES;
      viewId: string;
      newShowFieldNames: boolean;
    }
  | {
      type: ViewChangeType.UPDATE_LAYOUT_GROUP_FIELD_ID;
      viewId: string;
      newGroupFieldId: any;
    }
  | {
      type: ViewChangeType.SET_GROUPING_FIELD;
      viewId: string;
      fieldId: string;
      manualGroupOrder: any[];
      isSubgroup?: boolean;
    }
  | {
      type: ViewChangeType.SET_GROUPING_SORT_TYPE;
      viewId: string;
      sortType: any;
      isSubgroup?: boolean;
    }
  | {
      type: ViewChangeType.SET_GROUPING_MANUAL_GROUP_ORDER;
      viewId: string;
      manualGroupOrder: any[];
      isSubgroup?: boolean;
    }
  | { type: ViewChangeType.CLEAR_GROUPING; viewId: string; isSubgroup?: boolean }
  | {
      type: ViewChangeType.UPDATE_LAYOUT_DATE_FIELD_ID;
      viewId: string;
      newDateFieldId: any;
    }
  | { type: ViewChangeType.UPDATE_NAME; viewId: string; delta: any[] }
  | {
      type: ViewChangeType.UPDATE_FIELDS;
      viewId: string;
      fieldKey: string;
      fieldValue: boolean;
    }
  | { type: ViewChangeType.UPDATE_SORT; viewId: string; sort: any }
  | {
      type: ViewChangeType.UPDATE_SORT_VALUE;
      viewId: string;
      fieldId: string;
      ascending: boolean;
      customOrder?: boolean;
    }
  | { type: ViewChangeType.INSERT_SORT; viewId: string; index: number; sort: any }
  | { type: ViewChangeType.APPEND_SORT; viewId: string; sort: any }
  | { type: ViewChangeType.DELETE_SORT; viewId: string; fieldId: string }
  | {
      type: ViewChangeType.REORDER_SORT;
      viewId: string;
      fieldId: string;
      newIndex: number;
    }
  | { type: ViewChangeType.CLEAR_SORT; viewId: string }
  | {
      type: ViewChangeType.SET_FILTER_USAGE_TYPE;
      viewId: string;
      usageType: any;
      usageFilterId?: string;
      path?: number[];
    }
  | {
      type: ViewChangeType.SET_FILTER_VALUE;
      viewId: string;
      newFilter: any;
      path?: number[];
      change?: any;
    }
  | { type: ViewChangeType.SET_FILTER; viewId: string; newFilter: any; path?: number[] }
  | {
      type: ViewChangeType.APPEND_FILTER;
      viewId: string;
      newFilter: any;
      filterGroupId?: string;
      filterGroupPath?: number[];
    }
  | {
      type: ViewChangeType.REMOVE_FILTER;
      viewId: string;
      filterId: string;
      path?: number[];
    }
  | { type: ViewChangeType.CLEAR_FILTER; viewId: string }
  | { type: ViewChangeType.SET_CALCULATIONS; viewId: string; fieldId: string; value: any }
  | { type: ViewChangeType.REMOVE_CALCULATION; viewId: string; fieldId: string }
  | {
      type: ViewChangeType.UPDATE_LAYOUT_SHOW_NUMBERED_ENTRIES;
      viewId: string;
      newShowNumberedEntries: boolean;
    }
  | {
      /**
       * Full replacement of the view's properties bag.
       */
      type: ViewChangeType.SET_PROPERTIES;
      viewId: string;
      properties: Record<string, unknown> | undefined;
    }
  | {
      /**
       * Key-level patch of a view's properties (set/unset).
       */
      type: ViewChangeType.UPDATE_PROPERTIES;
      viewId: string;
      set?: Record<string, unknown>;
      unset?: string[];
    };

export function bulkApplySavedViewChanges(ydb: Database, changes: ViewChange[]): void {
  ydb.mutate((db) => {
    changes.forEach((viewChange) => {
      switch (viewChange.type) {
        case ViewChangeType.UPDATE_LAYOUT_TYPE: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            const collabType = CardTypeToCollabMapping[viewChange.newLayoutType];

            if (collabType) {
              view.layoutType = collabType;
            } else {
              view.layoutType = viewChange.newLayoutType;
            }
          }

          break;
        }

        case ViewChangeType.UPDATE_LAYOUT_CARD_WIDTH: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.cardWidth = CardWidthToCollabMapping[viewChange.newCardWidth];
          }

          break;
        }

        case ViewChangeType.UPDATE_LAYOUT_COVER_FIELD_ID: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.coverField = coverFieldIdToCollabMapping(viewChange.newCoverFieldId);
          }

          break;
        }

        case ViewChangeType.UPDATE_LAYOUT_SHOW_FIELD_NAMES: {
          ydb.mutate((db) => {
            const view = db.viewsById[viewChange.viewId];

            if (view) {
              view.showFieldNames = viewChange.newShowFieldNames;
            }
          });
          break;
        }

        case ViewChangeType.UPDATE_LAYOUT_GROUP_FIELD_ID: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.groupFieldId = viewChange.newGroupFieldId;
          }

          break;
        }

        case ViewChangeType.SET_GROUPING_FIELD: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            let grouping = view.grouping;
            if (!grouping) {
              grouping = view.initGrouping();
            }

            if (viewChange.isSubgroup) {
              grouping.subgroupByField(viewChange.fieldId, viewChange.manualGroupOrder);
            } else {
              grouping.groupByField(viewChange.fieldId, viewChange.manualGroupOrder);
            }
          }

          break;
        }

        case ViewChangeType.SET_GROUPING_SORT_TYPE: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            if (viewChange.isSubgroup) {
              const subgroupBy = view.grouping?.subgroupBy;
              if (subgroupBy) {
                subgroupBy.sortType = groupSortTypeToCollabMapping[viewChange.sortType];
              }
            } else {
              const groupBy = view.grouping?.groupBy;
              if (groupBy) {
                groupBy.sortType = groupSortTypeToCollabMapping[viewChange.sortType];
              }
            }
          }

          break;
        }

        case ViewChangeType.SET_GROUPING_MANUAL_GROUP_ORDER: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            if (viewChange.isSubgroup) {
              const subgroupBy = view.grouping?.subgroupBy;
              if (subgroupBy) {
                subgroupBy.manualGroupOrder = viewChange.manualGroupOrder;
              }
            } else {
              const groupBy = view.grouping?.groupBy;
              if (groupBy) {
                groupBy.manualGroupOrder = viewChange.manualGroupOrder;
              }
            }
          }

          break;
        }

        case ViewChangeType.CLEAR_GROUPING: {
          const view = db.viewsById[viewChange.viewId];

          if (view && view.grouping) {
            if (viewChange.isSubgroup) {
              view.grouping.clearSubgroupBy();
            } else {
              view.grouping.clearGroupBy();
            }
          }

          break;
        }

        case ViewChangeType.UPDATE_LAYOUT_DATE_FIELD_ID: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.dateFieldId = viewChange.newDateFieldId;
          }

          break;
        }

        case ViewChangeType.UPDATE_NAME: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.applyNameDelta(viewChange.delta);
          }

          break;
        }

        case ViewChangeType.UPDATE_FIELDS: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.setFieldVisibility(
              viewChange.fieldKey,
              viewChange.fieldValue,
              db.fields
            );
          }

          break;
        }

        case ViewChangeType.UPDATE_SORT: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.sort = viewChange.sort;
            if (viewChange.sort && view.sorts.length <= 1) {
              view.sorts.clearAll();
              view.sorts.appendSort(viewChange.sort);
            }
            if (!viewChange.sort && view.sorts.length > 0) {
              view.sorts.clearAll();
            }
          }

          break;
        }

        case ViewChangeType.UPDATE_SORT_VALUE: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.sorts.updateSortForFieldId(
              viewChange.fieldId,
              viewChange.ascending,
              viewChange.customOrder
            );
          }

          break;
        }

        case ViewChangeType.INSERT_SORT: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.sorts.insertSort(viewChange.index, viewChange.sort);
          }

          break;
        }

        case ViewChangeType.APPEND_SORT: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.sorts.appendSort(viewChange.sort);
          }

          break;
        }

        case ViewChangeType.DELETE_SORT: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.sorts.deleteSortForFieldId(viewChange.fieldId);
          }

          break;
        }

        case ViewChangeType.REORDER_SORT: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.sorts.reorderSortForFieldId(viewChange.fieldId, viewChange.newIndex);
          }

          break;
        }

        case ViewChangeType.CLEAR_SORT: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.sorts.clearAll();
          }

          break;
        }

        case ViewChangeType.SET_FILTER_USAGE_TYPE: {
          const view = db.viewsById[viewChange.viewId];
          let parentFilter = view.filter;

          if (view) {
            if (parentFilter) {
              parentFilter = parentFilter as MutableCollabFilterBooleanExpression;

              if (viewChange.usageFilterId && viewChange.path) {
                const nestedFilterData = getNestedFilter(
                  parentFilter.value,
                  viewChange.usageFilterId,
                  viewChange.path
                );

                if (nestedFilterData) {
                  const filter =
                    nestedFilterData[0] as MutableCollabFilterBooleanExpression;

                  if (
                    filter.type === CollabFilterType.AND ||
                    filter.type === CollabFilterType.OR
                  ) {
                    filter.type = filterUsageTypeToCollabMapping(viewChange.usageType);
                  }
                }
              } else {
                parentFilter.type = filterUsageTypeToCollabMapping(viewChange.usageType);
              }
            }
          }

          break;
        }

        case ViewChangeType.SET_FILTER_VALUE: {
          const view = db.viewsById[viewChange.viewId];
          let parentFilter = view.filter;
          const updatedFilterFromView = viewChange.newFilter;

          if (view) {
            if (!parentFilter || viewChange.path === undefined) {
              view.setFilter({
                id: uuidv4(),
                type: CollabFilterType.AND,
                value: []
              });
            } else {
              parentFilter = parentFilter as MutableCollabFilterBooleanExpression;
              const nestedFilterData = getNestedFilter(
                parentFilter.value,
                updatedFilterFromView.id,
                viewChange.path
              );

              if (nestedFilterData) {
                const filter = nestedFilterData[0] as MutableCollabFilter;
                if (
                  viewChange.change &&
                  (filter.type === CollabFilterType.EQUALS ||
                    filter.type === CollabFilterType.GREATER ||
                    filter.type === CollabFilterType.LESS)
                ) {
                  applyDeltaValue((type) => filter.setValue(type), viewChange.change);
                }

                if (
                  filter.type === CollabFilterType.CONTAINS &&
                  updatedFilterFromView.type === DatabaseFilterType.CONTAINS
                ) {
                  filter.value = updatedFilterFromView.value;
                }
              }
            }
          }

          break;
        }

        case ViewChangeType.SET_FILTER: {
          const view = db.viewsById[viewChange.viewId];
          let parentFilter = view.filter;

          if (view) {
            if (!viewChange.newFilter) {
              view.clearFilter();
            } else if (viewChange.path && viewChange.path.length === 0) {
              const collabFilter = filterToCollabJSON(viewChange.newFilter, {});
              if (collabFilter) {
                view.setFilter(collabFilter);
              }
            } else if (view.filter && viewChange.path) {
              parentFilter = parentFilter as MutableCollabFilterBooleanExpression;
              const nestedFilterData = getNestedFilter(
                parentFilter.value,
                viewChange.newFilter.id,
                viewChange.path
              );

              if (nestedFilterData) {
                const [filter, index] = nestedFilterData;
                const collabFilter = filterToCollabJSON(viewChange.newFilter, {});
                const directParent = getDirectParentFilter(
                  parentFilter,
                  filter,
                  index
                ) as MutableCollabFilterBooleanExpression | undefined;

                if (directParent) {
                  if (collabFilter) {
                    directParent.setFilter(filter.id, index, collabFilter);
                  } else {
                    directParent.remove(filter.id, index);

                    if (
                      directParent.value.length === 0 &&
                      directParent.id === view.filter.id
                    ) {
                      view.clearFilter();
                    }
                  }
                }
              }
            }
          }
          break;
        }

        case ViewChangeType.APPEND_FILTER: {
          const view = db.viewsById[viewChange.viewId];
          let parentFilter = view.filter;

          if (view) {
            parentFilter =
              (parentFilter as MutableCollabFilterBooleanExpression) ??
              (view.setFilter({
                id: uuidv4(),
                type: CollabFilterType.AND,
                value: []
              }) as MutableCollabFilterBooleanExpression);

            if (viewChange.filterGroupId && viewChange.filterGroupPath) {
              const nestedFilterData = getNestedFilter(
                parentFilter.value,
                viewChange.filterGroupId,
                viewChange.filterGroupPath
              );

              if (nestedFilterData) {
                const directParent =
                  nestedFilterData[0] as MutableCollabFilterBooleanExpression;

                const collabFilter = filterToCollabJSON(viewChange.newFilter, {});

                if (collabFilter) {
                  directParent.add(collabFilter);
                }
              }
            } else {
              const collabFilter = filterToCollabJSON(viewChange.newFilter, {});

              if (collabFilter) {
                parentFilter.add(collabFilter);
              }
            }
          }
          break;
        }

        case ViewChangeType.REMOVE_FILTER: {
          const view = db.viewsById[viewChange.viewId];
          let parentFilter = view.filter;

          if (view) {
            if (!parentFilter || !viewChange.path) {
              view.clearFilter();
            } else {
              parentFilter = parentFilter as MutableCollabFilterBooleanExpression;
              const nestedFilterData = getNestedFilter(
                parentFilter.value,
                viewChange.filterId,
                viewChange.path
              );

              if (nestedFilterData) {
                const [filter, index] = nestedFilterData;
                const directParent = getDirectParentFilter(
                  parentFilter,
                  filter,
                  index
                ) as MutableCollabFilterBooleanExpression | undefined;

                if (directParent) {
                  directParent.remove(viewChange.filterId, index);

                  if (directParent.value.length === 0) {
                    const filterGroupData = getNestedFilter(
                      parentFilter.value,
                      directParent.id,
                      viewChange.path.slice(0, -1)
                    );

                    if (filterGroupData) {
                      const [filterGroup, filterGroupIndex] = filterGroupData;

                      parentFilter.remove(filterGroup.id, filterGroupIndex);
                    }
                  }
                }
              }

              if (parentFilter.value.length === 0) {
                view.clearFilter();
              }
            }
          }

          break;
        }

        case ViewChangeType.CLEAR_FILTER: {
          const view = db.viewsById[viewChange.viewId];
          view.clearFilter();
          break;
        }

        case ViewChangeType.SET_CALCULATIONS:
          ydb.mutate((db) => {
            const view = db.viewsById[viewChange.viewId];

            if (view) {
              view.setCalculation(
                viewChange.fieldId,
                calculationToCollabMapping[viewChange.value]
              );
            }
          });
          break;

        case ViewChangeType.REMOVE_CALCULATION:
          ydb.mutate((db) => {
            const view = db.viewsById[viewChange.viewId];

            if (view) {
              view.removeCalculation(viewChange.fieldId);
            }
          });
          break;

        case ViewChangeType.UPDATE_LAYOUT_SHOW_NUMBERED_ENTRIES:
          ydb.mutate((db) => {
            const view = db.viewsById[viewChange.viewId];

            if (view) {
              view.showNumberedEntries = viewChange.newShowNumberedEntries;
            }
          });
          break;

        case ViewChangeType.SET_PROPERTIES: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.properties = viewChange.properties;
          }

          break;
        }

        case ViewChangeType.UPDATE_PROPERTIES: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.updateProperties({
              set: viewChange.set,
              unset: viewChange.unset
            });
          }

          break;
        }
      }
    });
  });
}
